import * as usBankRoutingNumber from './formats/usBankRoutingNumber';
import * as usBankAccountNumber from './formats/usBankAccountNumber';
import * as usDateMdy from './formats/usDateMdy';
import * as usStateAbbreviation from './formats/usStateAbbreviation';
import * as usSsn from './formats/usSsn';
import * as usZipCode from './formats/usZipCode';
import * as email from './formats/email';

export const REDACTED_VALUE = '*REDACTED*';
export const REDACTED_DISPLAY_LABEL = 'Value on File';

const FORMAT_MODULES = [
  usBankRoutingNumber,
  usBankAccountNumber,
  usDateMdy,
  usStateAbbreviation,
  usSsn,
  usZipCode,
  email,
];

const FORMATS = FORMAT_MODULES.reduce((acc, mod) => {
  acc[mod.key] = mod;
  return acc;
}, {});

export function getFormat(formatKey) {
  if (!formatKey) return null;
  return FORMATS[String(formatKey).toLowerCase()] || null;
}

export function getRegisteredFormatKey(formatKey) {
  const mod = getFormat(formatKey);
  return mod?.key || null;
}

export function listFormats() {
  return FORMAT_MODULES.reduce((acc, mod) => {
    acc[mod.key] = { ...mod.meta, key: mod.key };
    return acc;
  }, {});
}

export function isSensitive(formatKey) {
  const mod = getFormat(formatKey);
  return mod ? Boolean(mod.meta.sensitive) : false;
}

export function isAllowedForContactType(formatKey, type) {
  const mod = getFormat(formatKey);
  if (!mod) return false;
  return mod.meta.allowed_contact_types.includes(type);
}

export function isAllowedForFormType(formatKey, type) {
  const mod = getFormat(formatKey);
  if (!mod) return false;
  return mod.meta.allowed_form_types.includes(type);
}

export function validateFormat(formatKey, value, allowEmpty = true) {
  const mod = getFormat(formatKey);
  if (!mod) {
    return { valid: false, value, error: `Unknown format "${formatKey}"` };
  }

  const trimmed = value == null ? '' : String(value).trim();
  if (allowEmpty && trimmed === '') {
    return { valid: true, value: '', error: '' };
  }

  return mod.validate(value);
}

export function normalizeFormat(formatKey, value) {
  const mod = getFormat(formatKey);
  if (!mod) return value;
  const trimmed = value == null ? '' : String(value).trim();
  if (trimmed === '') return '';
  const res = mod.validate(value);
  return res.valid ? res.value : mod.normalize(value);
}

export function displayFormat(formatKey, value, options = {}) {
  const mod = getFormat(formatKey);
  if (value == null || value === '') return '';
  if (value === REDACTED_VALUE) {
    return options.context === 'edit' ? REDACTED_VALUE : REDACTED_DISPLAY_LABEL;
  }
  if (!mod) return String(value);
  return mod.display(value, options);
}

export function getInputProps(formatKey) {
  const mod = getFormat(formatKey);
  return mod && mod.getInputProps ? mod.getInputProps() : {};
}

/**
 * Resolve the effective format key for a field schema.
 * Prefer explicit schema.format; fall back to a mapped contact field in Vuex
 * when present. Only registered field-format keys are treated as formats,
 * because older schemas may use "format" for unrelated metadata such as slug.
 */
/** Formats that must stay digit strings — never coerce through JS Number(). */
const STRING_INPUT_FORMATS = new Set([
  'us_bank_routing_number',
  'us_bank_account_number',
  'us_ssn',
  'us_zip_code',
]);

export function formatRequiresStringInput(formatKey) {
  return STRING_INPUT_FORMATS.has(formatKey);
}

/**
 * Map form schema type to the field component base type (Text, Number, …).
 * Number schema fields with digit-string formats render as Text to preserve leading zeros.
 */
export function resolveFormFieldComponentType(schemaType, formatKey) {
  if (!schemaType) return '';
  if (
    formatKey &&
    formatRequiresStringInput(formatKey) &&
    schemaType === 'Number'
  ) {
    return 'Text';
  }
  return schemaType;
}

/**
 * RedactedFormatField replaces a secret value with plain-text entry; use Text
 * for the inner editor only — not for top-level FormField routing (admin
 * SecretField reveal must stay Secret).
 */
export function resolveRedactedInnerFieldComponentType(schemaType, formatKey) {
  const baseType =
    formatKey && String(schemaType).toLowerCase() === 'secret'
      ? 'Text'
      : schemaType || 'Text';

  return resolveFormFieldComponentType(baseType, formatKey);
}

export function resolveFieldFormat(schema = {}, context = {}) {
  if (!schema) return null;

  if (schema.format) {
    return getRegisteredFormatKey(schema.format);
  }

  const contactField = schema.contact_field || schema.field;
  const store = context.store;
  if (!contactField || !store?.getters) return null;

  const getter = store.getters['fields/getSingleField'];
  if (typeof getter !== 'function') return null;

  return getRegisteredFormatKey(getter(contactField)?.format);
}

export function isRedactedFormatValue(value, schema = {}) {
  if (value === REDACTED_VALUE) return true;
  return isSensitive(schema.format) && value === REDACTED_VALUE;
}

const FORMAT_SELECT_CONTACT_TYPES = ['string', 'int', 'secret', 'date', 'time'];

export function contactTypeSupportsFormat(type) {
  return FORMAT_SELECT_CONTACT_TYPES.includes(type);
}

export function getFormatOptionsForContactType(type, formatsFromApi = null) {
  const source = formatsFromApi || listFormats();
  return Object.entries(source)
    .filter(([, f]) => f.allowed_contact_types?.includes(type))
    .map(([key, f]) => ({ text: f.label, value: key }));
}

export function getFormatOptionsForFormType(type, formatsFromApi = null) {
  const source = formatsFromApi || listFormats();
  return Object.entries(source)
    .filter(([, f]) => f.allowed_form_types?.includes(type))
    .map(([key, f]) => ({ text: f.label, value: key }));
}

export function createFormatRule(formatKey, allowEmpty = true) {
  const rule = (value) => {
    const result = validateFormat(formatKey, value, allowEmpty);
    return result.valid ? true : result.error;
  };
  rule.formatKey = formatKey;
  return rule;
}
